import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Chip,
  Avatar,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ActivityFilePicker, { FileThumbnail } from './ActivityFilePicker';

const attachmentDisplayName = (file) => {
  if (file?.file_type !== 'plans') return file?.file_name || '';
  const extensionIndex = (file.file_name || '').lastIndexOf('.');
  return extensionIndex > 0 ? file.file_name.slice(0, extensionIndex) : file.file_name;
};

export const reorderChecklistItems = (items = [], activeId, overId) => {
  if (!overId || activeId === overId) {
    return items;
  }

  const oldIndex = items.findIndex((item) => item.id === activeId);
  const newIndex = items.findIndex((item) => item.id === overId);

  if (oldIndex === -1 || newIndex === -1) {
    return items;
  }

  return arrayMove(items, oldIndex, newIndex);
};

const SortableChecklistItem = ({
  item,
  isEditing,
  editValue,
  onEditStart,
  onEditChange,
  onEditCommit,
  onEditCancel,
  onToggle,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        px: 1.5,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        opacity: isDragging ? 0.85 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <IconButton
        size="small"
        aria-label={`Reorder checklist item ${item.text}`}
        sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Checkbox
        checked={Boolean(item.is_completed)}
        onChange={(event) => onToggle(item.id, event.target.checked)}
      />
      {isEditing ? (
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={editValue}
          onChange={(event) => onEditChange(event.target.value)}
          onBlur={() => onEditCommit(item.id, item.text)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onEditCommit(item.id, item.text);
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              onEditCancel();
            }
          }}
          inputProps={{
            'aria-label': `Edit checklist item ${item.text}`,
          }}
        />
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Button
            fullWidth
            variant="text"
            onClick={() => onEditStart(item)}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              color: 'text.primary',
              px: 0,
              py: 0.75,
              textAlign: 'left',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                textDecoration: item.is_completed ? 'line-through' : 'none',
                color: item.is_completed ? 'text.secondary' : 'text.primary',
              }}
            >
              {item.text}
            </Typography>
          </Button>
        </Box>
      )}
      <IconButton
        size="small"
        onClick={() => onRemove(item.id)}
        aria-label={`Remove checklist item ${item.text}`}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

/**
 * ActivityDialog - Reusable dialog component for creating/editing activities
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Handler for closing the dialog
 * @param {function} onSubmit - Handler for form submission
 * @param {object} formData - Current form data {title, description, due_date, is_completed, assigned_workers}
 * @param {function} onFormDataChange - Handler for form data changes
 * @param {array} workers - Available workers for assignment
 * @param {array} formTemplates - Available form templates for linking
 * @param {array} photos - Array of photo objects {preview, name, file}
 * @param {function} onPhotoSelect - Handler for photo selection
 * @param {function} onPhotoRemove - Handler for removing a photo
 * @param {boolean} submitting - Whether the form is currently submitting
 * @param {boolean} isEditing - Whether this is an edit (true) or create (false)
 */
const ActivityDialog = ({
  open,
  onClose,
  onSubmit,
  formData,
  onFormDataChange,
  workers = [],
  formTemplates = [],
  projectForms = [],
  projectFiles = [],
  documentFolders = [],
  projectFilesLoading = false,
  photos = [],
  onPhotoSelect,
  onPhotoRemove,
  submitting = false,
  isEditing = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedProjectFormId, setSelectedProjectFormId] = useState('');
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [editingChecklistItemId, setEditingChecklistItemId] = useState(null);
  const [editingChecklistValue, setEditingChecklistValue] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const linkedForms = useMemo(
    () => formData.linked_forms || [],
    [formData.linked_forms]
  );
  const existingPhotos = useMemo(
    () => formData.existing_photos || [],
    [formData.existing_photos]
  );
  const existingDocuments = useMemo(
    () => formData.existing_documents || [],
    [formData.existing_documents]
  );
  const pendingFileLinks = useMemo(
    () => formData.pending_file_links || [],
    [formData.pending_file_links]
  );
  const availableFiles = useMemo(() => {
    const linkedIds = new Set([
      ...existingDocuments.map((file) => file.file_id),
      ...pendingFileLinks.map((file) => file.file_id),
    ].map(String));
    return projectFiles.filter((file) => file.file_id != null && ['plans', 'photos'].includes(file.file_type)
      && file.activity_id == null && !linkedIds.has(String(file.file_id)));
  }, [existingDocuments, pendingFileLinks, projectFiles]);
  const pendingFormTemplates = useMemo(
    () => formData.pending_form_templates || [],
    [formData.pending_form_templates]
  );
  const pendingExistingForms = useMemo(
    () => formData.pending_existing_forms || [],
    [formData.pending_existing_forms]
  );
  const availableProjectForms = useMemo(() => {
    const linkedIds = new Set(
      linkedForms
        .map((form) => form.project_form_id ?? form.id)
        .filter((id) => id != null)
        .map(String)
    );
    const pendingIds = new Set(
      pendingExistingForms
        .map((form) => form.project_form_id ?? form.id)
        .filter((id) => id != null)
        .map(String)
    );

    return (projectForms || []).filter((form) => {
      const formId = form.project_form_id ?? form.id;
      if (formId == null) {
        return false;
      }

      const normalizedId = String(formId);
      if (linkedIds.has(normalizedId) || pendingIds.has(normalizedId)) {
        return false;
      }

      return true;
    });
  }, [linkedForms, pendingExistingForms, projectForms]);

  useEffect(() => {
    if (!open) {
      setSelectedTemplateId('');
      setSelectedProjectFormId('');
      setFilePickerOpen(false);
      setNewChecklistItem('');
      setEditingChecklistItemId(null);
      setEditingChecklistValue('');
    }
  }, [open]);

  const handlePhotoSelectWrapper = useCallback(
    async (event) => {
      if (onPhotoSelect) {
        await onPhotoSelect(event);
      }
    },
    [onPhotoSelect]
  );

  const handleRemovePhoto = useCallback(
    (index) => {
      if (onPhotoRemove) {
        onPhotoRemove(index);
      }
    },
    [onPhotoRemove]
  );

  const handleAddFormTemplate = useCallback(() => {
    const template = formTemplates.find(
      (item) => String(item.template_id) === String(selectedTemplateId)
    );
    if (!template) {
      return;
    }

    const attachmentId = `template-${template.template_id}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    onFormDataChange({
      ...formData,
      pending_form_templates: [
        ...pendingFormTemplates,
        {
          attachment_id: attachmentId,
          template_id: template.template_id,
          name: template.name,
          description: template.description,
          form_schema: template.form_schema,
        },
      ],
    });
    setSelectedTemplateId('');
  }, [formData, formTemplates, onFormDataChange, pendingFormTemplates, selectedTemplateId]);

  const handleRemovePendingFormTemplate = useCallback(
    (attachmentId) => {
      onFormDataChange({
        ...formData,
        pending_form_templates: pendingFormTemplates.filter(
          (item) => item.attachment_id !== attachmentId
        ),
      });
    },
    [formData, onFormDataChange, pendingFormTemplates]
  );

  const handleAddExistingForm = useCallback(() => {
    const form = availableProjectForms.find(
      (item) => String(item.project_form_id ?? item.id) === String(selectedProjectFormId)
    );
    if (!form) {
      return;
    }

    onFormDataChange({
      ...formData,
      pending_existing_forms: [
        ...pendingExistingForms,
        {
          project_form_id: form.project_form_id ?? form.id,
          title: form.title,
          template_name: form.template_name || form.template?.name,
        },
      ],
    });
    setSelectedProjectFormId('');
  }, [availableProjectForms, formData, onFormDataChange, pendingExistingForms, selectedProjectFormId]);

  const handleRemovePendingExistingForm = useCallback(
    (formId) => {
      onFormDataChange({
        ...formData,
        pending_existing_forms: pendingExistingForms.filter(
          (form) => String(form.project_form_id ?? form.id) !== String(formId)
        ),
      });
    },
    [formData, onFormDataChange, pendingExistingForms]
  );

  const handleRemoveExistingPhoto = useCallback(
    (photoId) => {
      onFormDataChange({
        ...formData,
        existing_photos: existingPhotos.filter((photo) => photo.photo_id !== photoId),
        removed_photo_ids: [...(formData.removed_photo_ids || []), photoId],
      });
    },
    [existingPhotos, formData, onFormDataChange]
  );

  const handleRemoveExistingDocument = useCallback(
    (fileId) => {
      onFormDataChange({
        ...formData,
        existing_documents: existingDocuments.filter((document) => document.file_id !== fileId),
        removed_document_ids: [...(formData.removed_document_ids || []), fileId],
      });
    },
    [existingDocuments, formData, onFormDataChange]
  );

  const handleAddExistingFiles = useCallback((selectedFiles) => {
    if (!selectedFiles.length) return;
    onFormDataChange({
      ...formData,
      pending_file_links: [...pendingFileLinks, ...selectedFiles],
    });
  }, [formData, onFormDataChange, pendingFileLinks]);

  const handleRemoveLinkedForm = useCallback(
    (formId) => {
      onFormDataChange({
        ...formData,
        linked_forms: linkedForms.filter((form) => form.project_form_id !== formId),
        removed_form_ids: [...(formData.removed_form_ids || []), formId],
      });
    },
    [formData, linkedForms, onFormDataChange]
  );

  const handleAddChecklistItem = useCallback(() => {
    const text = newChecklistItem.trim();
    if (!text) {
      return;
    }

    const checklistItemId =
      typeof window !== 'undefined' && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `checklist-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

    onFormDataChange({
      ...formData,
      checklist_items: [
        ...(formData.checklist_items || []),
        {
          id: checklistItemId,
          text,
          is_completed: false,
        },
      ],
    });
    setNewChecklistItem('');
  }, [formData, newChecklistItem, onFormDataChange]);

  const handleChecklistItemToggle = useCallback(
    (itemId, isCompleted) => {
      onFormDataChange({
        ...formData,
        checklist_items: (formData.checklist_items || []).map((item) =>
          item.id === itemId ? { ...item, is_completed: isCompleted } : item
        ),
      });
    },
    [formData, onFormDataChange]
  );

  const handleChecklistItemRemove = useCallback(
    (itemId) => {
      if (editingChecklistItemId === itemId) {
        setEditingChecklistItemId(null);
        setEditingChecklistValue('');
      }

      onFormDataChange({
        ...formData,
        checklist_items: (formData.checklist_items || []).filter((item) => item.id !== itemId),
      });
    },
    [editingChecklistItemId, formData, onFormDataChange]
  );

  const handleChecklistEditStart = useCallback((item) => {
    setEditingChecklistItemId(item.id);
    setEditingChecklistValue(item.text);
  }, []);

  const handleChecklistEditCancel = useCallback(() => {
    setEditingChecklistItemId(null);
    setEditingChecklistValue('');
  }, []);

  const handleChecklistEditCommit = useCallback(
    (itemId, previousText) => {
      const trimmedText = editingChecklistValue.trim();
      const nextText = trimmedText || previousText;

      onFormDataChange({
        ...formData,
        checklist_items: (formData.checklist_items || []).map((item) =>
          item.id === itemId ? { ...item, text: nextText } : item
        ),
      });
      setEditingChecklistItemId(null);
      setEditingChecklistValue('');
    },
    [editingChecklistValue, formData, onFormDataChange]
  );

  const handleChecklistDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over) {
        return;
      }

      const reorderedItems = reorderChecklistItems(
        formData.checklist_items || [],
        active.id,
        over.id
      );

      if (reorderedItems === (formData.checklist_items || [])) {
        return;
      }

      onFormDataChange({
        ...formData,
        checklist_items: reorderedItems,
      });
    },
    [formData, onFormDataChange]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 3 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {isEditing ? 'Edit Activity' : 'New Activity'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) =>
                  onFormDataChange({ ...formData, title: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  onFormDataChange({ ...formData, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateTimePicker
                label="Due Date"
                value={formData.due_date}
                onChange={(date) =>
                  onFormDataChange({ ...formData, due_date: date })
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_completed}
                    onChange={(e) =>
                      onFormDataChange({
                        ...formData,
                        is_completed: e.target.checked,
                      })
                    }
                  />
                }
                label="Completed"
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={workers || []}
                getOptionLabel={(worker) => worker?.name || ''}
                isOptionEqualToValue={(option, value) =>
                  option?.worker_id === value?.worker_id
                }
                value={
                  workers?.filter((w) =>
                    formData.assigned_workers?.includes(w.worker_id)
                  ) || []
                }
                onChange={(event, newValue) => {
                  onFormDataChange({
                    ...formData,
                    assigned_workers: newValue.map((w) => w.worker_id),
                  });
                }}
                filterSelectedOptions={false}
                noOptionsText="No team members available"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign to Team Members"
                    placeholder="Select team members..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((worker, index) => (
                    <Chip
                      key={worker.worker_id}
                      label={worker.name}
                      {...getTagProps({ index })}
                      avatar={<Avatar>{worker.name.charAt(0)}</Avatar>}
                    />
                  ))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Checklist
              </Typography>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={9}>
                  <TextField
                    fullWidth
                    size="small"
                    label="New Checklist Item"
                    value={newChecklistItem}
                    onChange={(event) => setNewChecklistItem(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleAddChecklistItem();
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleAddChecklistItem}
                    disabled={!newChecklistItem.trim()}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Add Item
                  </Button>
                </Grid>
              </Grid>

              {(formData.checklist_items || []).length > 0 && (
                <Box sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleChecklistDragEnd}
                  >
                    <SortableContext
                      items={(formData.checklist_items || []).map((item) => item.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {(formData.checklist_items || []).map((item) => (
                        <SortableChecklistItem
                          key={item.id}
                          item={item}
                          isEditing={editingChecklistItemId === item.id}
                          editValue={editingChecklistValue}
                          onEditStart={handleChecklistEditStart}
                          onEditChange={setEditingChecklistValue}
                          onEditCommit={handleChecklistEditCommit}
                          onEditCancel={handleChecklistEditCancel}
                          onToggle={handleChecklistItemToggle}
                          onRemove={handleChecklistItemRemove}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Forms
              </Typography>

              {linkedForms.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  {linkedForms.map((form) => (
                    <Chip
                      key={form.project_form_id}
                      icon={<DescriptionIcon />}
                      label={form.title || form.template_name || 'Linked Form'}
                      variant="outlined"
                      deleteIcon={<CloseIcon data-testid={`remove-form-${form.project_form_id}`} />}
                      onDelete={() => handleRemoveLinkedForm(form.project_form_id)}
                    />
                  ))}
                </Box>
              )}

              {pendingFormTemplates.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  {pendingFormTemplates.map((template) => (
                    <Chip
                      key={template.attachment_id}
                      color="primary"
                      label={template.name}
                      onDelete={() =>
                        handleRemovePendingFormTemplate(template.attachment_id)
                      }
                    />
                  ))}
                </Box>
              )}

              {pendingExistingForms.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  {pendingExistingForms.map((form) => (
                    <Chip
                      key={form.project_form_id || form.id}
                      color="secondary"
                      icon={<DescriptionIcon />}
                      label={form.title || form.template_name || 'Existing Form'}
                      onDelete={() => handleRemovePendingExistingForm(form.project_form_id || form.id)}
                    />
                  ))}
                </Box>
              )}

              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="activity-form-template-label">
                      Form Template
                    </InputLabel>
                    <Select
                      labelId="activity-form-template-label"
                      value={selectedTemplateId}
                      label="Form Template"
                      onChange={(event) => setSelectedTemplateId(event.target.value)}
                    >
                      {formTemplates.map((template) => (
                        <MenuItem
                          key={template.template_id}
                          value={String(template.template_id)}
                        >
                          {template.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleAddFormTemplate}
                    disabled={!selectedTemplateId}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Attach Form
                  </Button>
                </Grid>
              </Grid>

              <Grid container spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <Grid item xs={12} sm={8}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="activity-existing-form-label">
                      Existing Project Form
                    </InputLabel>
                    <Select
                      labelId="activity-existing-form-label"
                      value={selectedProjectFormId}
                      label="Existing Project Form"
                      onChange={(event) => setSelectedProjectFormId(event.target.value)}
                    >
                      {availableProjectForms.map((form) => (
                        <MenuItem
                          key={form.project_form_id || form.id}
                          value={String(form.project_form_id || form.id)}
                        >
                          {form.title || form.template_name || form.template?.name || `Form ${form.project_form_id || form.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleAddExistingForm}
                    disabled={!selectedProjectFormId}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Attach Existing
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Photos Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Attachments
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {existingPhotos.map((photo) => (
                  <Box
                    key={photo.photo_id}
                    sx={{ position: 'relative', width: 80, height: 80 }}
                  >
                    <img
                      src={photo.url || photo.blob_path}
                      alt={photo.file_name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid rgba(0,0,0,0.12)',
                      }}
                    />
                    <IconButton
                      onClick={() => handleRemoveExistingPhoto(photo.photo_id)}
                      aria-label={`Remove ${photo.file_name}`}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' },
                        width: 24,
                        height: 24,
                      }}
                      size="small"
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                ))}
                {photos.map((photo, index) => (
                  <Box
                    key={index}
                    sx={{ position: 'relative', width: 80, height: 80 }}
                  >
                    {photo.preview ? (
                      <img
                        src={photo.preview}
                        alt={photo.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '1px solid rgba(0,0,0,0.12)',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '8px',
                          border: '1px solid rgba(0,0,0,0.12)',
                          bgcolor: 'action.hover',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 1,
                          textAlign: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        <DescriptionIcon color="action" sx={{ mb: 0.5 }} />
                        <Typography variant="caption" sx={{ lineHeight: 1.1, wordBreak: 'break-word' }}>
                          {photo.name}
                        </Typography>
                      </Box>
                    )}
                    <IconButton
                      onClick={() => handleRemovePhoto(index)}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' },
                        width: 24,
                        height: 24,
                      }}
                      size="small"
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                ))}

                {existingDocuments.filter((document) => document.file_type === 'photos').map((document) => (
                  <Box key={document.file_id} sx={{ position: 'relative', width: 80, height: 80, borderRadius: 2, overflow: 'visible' }}>
                    <Box sx={{ width: '100%', height: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                      <FileThumbnail file={document} />
                    </Box>
                    <IconButton size="small" onClick={() => handleRemoveExistingDocument(document.file_id)}
                      aria-label={`Remove ${document.file_name}`}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                ))}

                {existingDocuments.filter((document) => document.file_type !== 'photos').map((document) => (
                  <Box
                    key={document.file_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 2,
                      border: '1px solid rgba(0,0,0,0.12)',
                      bgcolor: 'action.hover',
                    }}
                  >
                    <DescriptionIcon color="action" />
                    <Typography variant="caption" sx={{ maxWidth: 180, wordBreak: 'break-word' }}>
                      {attachmentDisplayName(document)}
                    </Typography>
                    <IconButton
                      onClick={() => handleRemoveExistingDocument(document.file_id)}
                      aria-label={`Remove ${document.file_name}`}
                      size="small"
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                ))}

                {pendingFileLinks.filter((file) => file.file_type === 'photos').map((file) => (
                  <Box key={file.file_id} sx={{ position: 'relative', width: 80, height: 80 }}>
                    <Box sx={{ width: '100%', height: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                      <FileThumbnail file={file} />
                    </Box>
                    <IconButton size="small" aria-label={`Remove ${file.file_name}`}
                      onClick={() => onFormDataChange({ ...formData, pending_file_links: pendingFileLinks.filter((item) => item.file_id !== file.file_id) })}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                ))}

                {pendingFileLinks.filter((file) => file.file_type !== 'photos').map((file) => (
                  <Chip
                    key={file.file_id}
                    icon={<DescriptionIcon />}
                    label={attachmentDisplayName(file)}
                    color="primary"
                    variant="outlined"
                    onDelete={() => onFormDataChange({
                      ...formData,
                      pending_file_links: pendingFileLinks.filter((item) => item.file_id !== file.file_id),
                    })}
                  />
                ))}

                <input
                  accept="image/*,application/pdf"
                  style={{ display: 'none' }}
                  id="activity-dialog-photo-upload"
                  multiple
                  type="file"
                  onChange={handlePhotoSelectWrapper}
                />
                <label htmlFor="activity-dialog-photo-upload">
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <AddIcon color="action" />
                  </Box>
                </label>
              </Box>
              <Button variant="outlined" startIcon={<FolderOpenIcon />} sx={{ mt: 2, textTransform: 'none' }}
                onClick={() => setFilePickerOpen(true)}>
                Choose from project plans or photos
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={submitting || !formData.title.trim()}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
            }}
          >
            {submitting
              ? 'Saving...'
              : isEditing
              ? 'Update Activity'
              : 'Create Activity'}
          </Button>
        </DialogActions>
      </Dialog>
      <ActivityFilePicker
        open={filePickerOpen && open}
        onClose={() => setFilePickerOpen(false)}
        onAttach={handleAddExistingFiles}
        files={availableFiles}
        folders={documentFolders}
        loading={projectFilesLoading}
      />
    </LocalizationProvider>
  );
};

export default ActivityDialog;
